import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Card,
  Flex,
  Progress,
  Space,
  Tooltip,
  Typography
} from 'antd'
import { ArrowRight, Eye, Star } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { HandoffPath, track } from 'src/analytics'
import { applicationsApplicationsListOptions } from 'src/api/main'
import { SetupProgressStepperModal } from 'src/features/organizations/components'
import { paths } from 'src/router'

import {
  AT,
  BuildRing,
  EASE,
  popIn,
  riseIn,
  sheenSweep,
  slideRight,
  stepDelay,
  swap,
  type BuildActivity
} from '../done'
import { PulseDot } from '../done/PulseDot'
import { useStyles as useDoneStyles } from '../done/styles'
import { DONE_CARD_INSET, useStyles } from '../styles'
import type { CreateWorkspaceOutcome } from '../useCreateWorkspace'

import { StepHeader } from './StepHeader'

const { Text } = Typography

/** Which installed app the CRM card hands off to. */
const CRM_APP = /crm/i

type Props = {
  outcome: CreateWorkspaceOutcome
  /** Name of the chosen demo-data preset, or null for a clean workspace. */
  presetName: string | null
  activity: BuildActivity
  photoUrl?: string
}

/**
 * Terminal screen. Every line of the checklist reports something that actually
 * happened during `useCreateWorkspace` — nothing is asserted optimistically.
 *
 * It arrives a piece at a time, on the source design's timing map: the mark, the
 * headline, each line of the summary with its tick drawing itself, the build,
 * the two ways on. The workspace was assembled over several requests, and the
 * screen says so.
 *
 * The provisioning run is reported twice over: as a bar here, and step by step
 * in `BuildActivityPanel` beside it. Until it settles the handoff cards and the
 * primary action are held shut — offering a workspace that is still being
 * seeded reads as a broken link rather than a head start.
 */
export const DoneScreen = ({
  outcome,
  presetName,
  activity,
  photoUrl
}: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { styles: doneStyles } = useDoneStyles()
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()

  const [progressOpen, setProgressOpen] = useState(false)

  const animates = !reduceMotion
  const isLocked = !activity.canOpenWorkspace

  const workspaceId = String(outcome.organization.id)

  const overviewPath = paths.workspaces.overview(workspaceId)

  /**
   * Which app the CRM card opens. Asked only once the build has settled — the
   * apps are installed by that same run, so asking earlier reliably answers
   * "none yet". Until it resolves the card falls back to the apps list, which is
   * never wrong, only less specific.
   */
  const { data: apps } = useQuery({
    ...applicationsApplicationsListOptions({
      query: { organization: Number(outcome.organization.id), per_page: 100 }
    }),
    enabled: activity.canOpenWorkspace
  })

  const crmApp = (apps?.results ?? []).find(({ name }) => CRM_APP.test(name))

  const crmPath = crmApp
    ? paths.workspaces.apps.settings(workspaceId, String(crmApp.id))
    : paths.workspaces.apps.root(workspaceId)

  const checks = [
    t('workspaces.onboarding.done.checks.created', {
      name: outcome.organization.name
    }),
    t('workspaces.onboarding.done.checks.owner'),
    presetName
      ? t('workspaces.onboarding.done.checks.template', { preset: presetName })
      : t('workspaces.onboarding.done.checks.cleanWorkspace'),
    outcome.invitesSent > 0
      ? t('workspaces.onboarding.done.checks.invites', {
          count: outcome.invitesSent
        })
      : t('workspaces.onboarding.done.checks.noInvites')
  ]

  const warnings = [
    outcome.profileFailed
      ? t('workspaces.onboarding.done.profileFailed')
      : null,
    outcome.invitesFailed
      ? t('workspaces.onboarding.done.invitesFailed')
      : null,
    outcome.photoFailed ? t('workspaces.onboarding.done.photoFailed') : null,
    outcome.authApplied ? null : t('workspaces.onboarding.done.authFailed')
  ].filter((warning): warning is string => warning !== null)

  const cards = [
    {
      key: HandoffPath.CRM,
      title: t('workspaces.onboarding.done.cards.crm.title'),
      badge: t('workspaces.onboarding.done.cards.crm.badge'),
      items: ['import', 'pipeline', 'messengers', 'email'],
      to: crmPath
    },
    {
      key: HandoffPath.WORKSPACE,
      title: t('workspaces.onboarding.done.cards.workspace.title'),
      badge: null,
      items: ['invite', 'roles', 'auth', 'settings'],
      // The workspace overview, not the members list: the card covers four
      // different settings and the overview is the one page all of them hang
      // off, so it is the honest landing spot for "set up your workspace".
      to: overviewPath
    }
  ] as const

  /**
   * The sentence above the bar. The running variant carries a parenthetical
   * ("… (loading demo data)"), which is what gives way first when the row is
   * narrow — it truncates to an ellipsis rather than wrapping and shoving the
   * percentage and the details button into a column of their own.
   */
  const setupLabel = activity.canOpenWorkspace
    ? t('workspaces.onboarding.done.setupFinished')
    : t('workspaces.onboarding.done.setupRunning')

  const buildLabel = activity.hasFailedStep
    ? t('workspaces.onboarding.done.build.failed')
    : activity.canOpenWorkspace
      ? t('workspaces.onboarding.done.build.ready')
      : activity.runningKind
        ? t(
            `workspaces.onboarding.done.build.stepNames.${activity.runningKind}`
          )
        : t('workspaces.onboarding.done.build.starting')

  return (
    <>
      {/* Fills the pane so the actions below can sit on its bottom edge, the
          same place the other steps put their primary button. */}
      <Flex vertical gap={16} flex={1}>
        {/* Mark and headline on one line: together they are the single claim
            this screen opens with. */}
        <Flex align="center" gap={16}>
          <motion.div {...popIn(animates, AT.ring)}>
            <BuildRing
              workspaceName={outcome.organization.name}
              photoUrl={photoUrl}
              percent={activity.percent}
              isComplete={activity.canOpenWorkspace}
              hasFailed={activity.hasFailedStep}
              animates={animates}
            />
          </motion.div>

          <motion.div {...riseIn(animates, AT.title)}>
            {/* The headline follows the build rather than announcing its
                outcome up front: while the environment is still being seeded
                the workspace is not ready, and saying so anyway is the one
                claim on this screen that would be untrue. */}
            <StepHeader
              title={t(
                isLocked
                  ? 'workspaces.onboarding.done.titleWorking'
                  : 'workspaces.onboarding.done.title',
                { name: outcome.organization.name }
              )}
            />
          </motion.div>
        </Flex>

        {/* Plain rows, not `List` — AntD v6 deprecates that component.
            `ph-no-capture` because the first line names the workspace, and
            `mask_all_text` stays off so the rest of this screen is readable in
            replay. Which checks passed is already on `onboarding_completed`; the
            warnings below stay visible. */}
        <Flex vertical gap={8} className="ph-no-capture">
          {checks.map((line, index) => {
            const delay = stepDelay(AT.firstCheck, AT.checkGap, index)

            return (
              <motion.div key={line} {...slideRight(animates, delay)}>
                <Space align="start" size={10}>
                  {/* The tick draws itself rather than fading in — the design
                      asks for the summary to be written, not revealed. */}
                  <svg
                    width={15}
                    height={15}
                    viewBox="0 0 24 24"
                    fill="none"
                    className={doneStyles.checkGlyph}
                    aria-hidden
                  >
                    <motion.path
                      d="M20 6 9 17l-5-5"
                      stroke="var(--ant-color-primary)"
                      strokeWidth={2.4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: animates ? 0 : 1 }}
                      animate={{ pathLength: 1 }}
                      transition={
                        animates
                          ? {
                              duration: 0.3,
                              delay: delay + AT.checkDraw,
                              ease: EASE
                            }
                          : { duration: 0 }
                      }
                    />
                  </svg>
                  <Text type="secondary">{line}</Text>
                </Space>
              </motion.div>
            )
          })}
        </Flex>

        {/* The environment build outlives this screen, so it reports itself
            here rather than holding the user behind a modal — which stays one
            click away for the step list and the retry. */}
        {outcome.setupJobId !== undefined ? (
          <motion.div {...riseIn(animates, AT.progress)}>
            <Flex vertical gap={10}>
              {/* Centred, not baseline-aligned: the label is an ellipsised
                  block, and a block with clipped overflow takes its bottom
                  edge as its baseline — which would drop it out of line with
                  the percentage beside it. */}
              <Flex align="center" justify="space-between" gap={12}>
                <Text
                  type="secondary"
                  className={doneStyles.setupLabel}
                  ellipsis={{ tooltip: setupLabel }}
                >
                  {setupLabel}
                </Text>
                <Space size={8} className={doneStyles.setupMeta}>
                  <Text className={doneStyles.stepStatus}>
                    {`${activity.percent} %`}
                  </Text>
                  <Button
                    size="small"
                    icon={<Eye size={14} />}
                    onClick={() => setProgressOpen(true)}
                  >
                    {t('workspaces.onboarding.done.setupView')}
                  </Button>
                </Space>
              </Flex>

              <Progress
                percent={activity.percent}
                size="small"
                showInfo={false}
                status={
                  activity.hasFailedStep
                    ? 'exception'
                    : activity.canOpenWorkspace
                      ? 'success'
                      : 'active'
                }
              />

              <Flex align="center" gap={9} style={{ height: 18 }}>
                <PulseDot
                  animates={animates && !activity.canOpenWorkspace}
                  tone={activity.hasFailedStep ? 'error' : 'primary'}
                />
                <AnimatePresence mode="wait">
                  <motion.span key={buildLabel} {...swap(animates)}>
                    <Text type="secondary" className={doneStyles.phaseLabel}>
                      {buildLabel}
                    </Text>
                  </motion.span>
                </AnimatePresence>
              </Flex>
            </Flex>
          </motion.div>
        ) : null}

        {warnings.map((warning) => (
          <motion.div key={warning} {...riseIn(animates, AT.progress)}>
            <Alert type="warning" showIcon message={warning} />
          </motion.div>
        ))}

        {/* Side by side: the two are alternatives, not a sequence, and the
            card widens on this step precisely so they can be compared. */}
        <div className={styles.doneCardGrid} aria-disabled={isLocked}>
          {cards.map((card, cardIndex) => (
            <motion.div
              key={card.key}
              {...riseIn(
                animates,
                stepDelay(AT.firstCard, AT.cardGap, cardIndex),
                0.55
              )}
              style={{ pointerEvents: isLocked ? 'none' : 'auto' }}
            >
              <motion.div
                className={styles.doneCard}
                animate={{ opacity: isLocked ? 0.45 : 1 }}
                transition={{ duration: 0.3, ease: EASE }}
              >
                {/* A real anchor, not a div with onClick: the two cards are
                    navigation, and the browser should say where they lead in the
                    status bar before anyone commits to the click. */}
                <Link
                  to={card.to}
                  className={doneStyles.cardLink}
                  onClick={() =>
                    track('onboarding_path_chosen', { path: card.key })
                  }
                >
                  <Card
                    hoverable
                    size="small"
                    className={styles.doneCard}
                    styles={{
                      header: DONE_CARD_INSET,
                      body: DONE_CARD_INSET
                    }}
                    // The title takes the row and truncates if it must; the
                    // recommendation is a fixed 14px glyph that says what it
                    // means on hover — see `doneCardBadge`.
                    title={
                      <Flex align="center" gap={8}>
                        <Text
                          strong
                          className={doneStyles.rowLabel}
                          ellipsis={{ tooltip: card.title }}
                        >
                          {card.title}
                        </Text>
                        {card.badge ? (
                          <Tooltip title={card.badge}>
                            <motion.span
                              className={styles.doneCardBadge}
                              {...popIn(animates, AT.cardBadge)}
                            >
                              {/* Carries the label itself: an icon on its own
                                  is the one piece of this row a screen reader
                                  would otherwise skip. */}
                              <Star
                                size={14}
                                strokeWidth={1.5}
                                role="img"
                                aria-label={card.badge}
                              />
                            </motion.span>
                          </Tooltip>
                        ) : null}
                      </Flex>
                    }
                  >
                    <Flex vertical gap={6} flex={1}>
                      {card.items.map((item, itemIndex) => {
                        const label = t(
                          `workspaces.onboarding.done.cards.${card.key}.items.${item}`
                        )

                        return (
                          <motion.div
                            key={item}
                            {...slideRight(
                              animates,
                              stepDelay(
                                AT.firstCardLink + cardIndex * AT.cardGap,
                                AT.cardLinkGap,
                                itemIndex
                              )
                            )}
                          >
                            {/* One line each, the rest on hover. Half of these
                                labels wrap in Russian, which turned a list of
                                four into seven lines of uneven height and made
                                the two cards stop matching. */}
                            <Text
                              type="secondary"
                              ellipsis={{ tooltip: label }}
                            >
                              · {label}
                            </Text>
                          </motion.div>
                        )
                      })}
                    </Flex>
                  </Card>
                </Link>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className={doneStyles.actionShell}
          style={{ marginBlockStart: 'auto' }}
          {...riseIn(animates, AT.action)}
        >
          <Button
            block
            type="primary"
            size="large"
            loading={isLocked}
            icon={isLocked ? undefined : <ArrowRight size={14} />}
            iconPlacement="end"
            // `href` so the destination shows in the status bar; the click is
            // still handled in-app, so the anchor never actually navigates.
            href={isLocked ? undefined : overviewPath}
            // Neither track — going straight to the workspace is a third answer
            // to the fork, and lumping it in with either card would overstate
            // that card's pull.
            onClick={(event) => {
              event.preventDefault()
              if (isLocked) return
              track('onboarding_path_chosen', { path: HandoffPath.OVERVIEW })
              navigate(overviewPath)
            }}
          >
            {isLocked
              ? t('workspaces.onboarding.done.build.preparing')
              : t('workspaces.onboarding.done.open')}
          </Button>

          {/* One pass of light at the moment the workspace becomes openable —
              the design's way of saying the wait is over. */}
          {!isLocked && animates ? (
            <motion.span
              className={doneStyles.actionSheen}
              {...sheenSweep(animates)}
            />
          ) : null}
        </motion.div>
      </Flex>

      <SetupProgressStepperModal
        key={outcome.setupJobId ?? 'idle'}
        open={progressOpen}
        onCancel={() => setProgressOpen(false)}
        parentId={outcome.setupJobId}
        organization={outcome.organization}
      />
    </>
  )
}
